import { useSyncExternalStore } from 'react';
import { SCHEMA_VERSION, type QaEvent, type QaEventType, type QaManifest } from './events';
import { encodeWav, zip } from './files';
import { indexedDbStore, memoryStore, type Chunk, type QaStore, type StoredSession } from './store';

/** Sample rate of the recorded microphone track: what the scorer receives. */
export const QA_SAMPLE_RATE = 16000;
/** Buffered records are written to the store this often (a crash loses at most this much). */
const FLUSH_MS = 2000;
/** Input levels are logged at most this often. */
const LEVEL_MS = 50;

/** Where finished sessions go. */
export interface QaTransport {
  name: string;
  available(): Promise<boolean>;
  put(session: string, path: string, data: Uint8Array<ArrayBuffer>): Promise<void>;
}

/** The local Vite dev server writes sessions to data/qa/sessions (vite.config.ts). */
export const devServerTransport: QaTransport = {
  name: 'this computer (data/qa)',
  available: async () => {
    try {
      return (await fetch('/__qa/ping')).ok;
    } catch {
      return false;
    }
  },
  put: async (session, path, data) => {
    const r = await fetch(`/__qa/upload/${session}/${path}`, { method: 'PUT', body: data });
    if (!r.ok) throw new Error(`upload ${path}: HTTP ${String(r.status)}`);
  },
};

interface Active {
  manifest: QaManifest;
  t0: number;
  seq: number;
  events: number;
  utterances: number;
  micSamples: number;
  eventLines: string[];
  levelLines: string[];
  mic: Float32Array[];
  lastLevel: number;
  timer: ReturnType<typeof setInterval>;
}

export interface QaState {
  recording: boolean;
  /** performance.now() when the active session started. */
  startedAt: number | undefined;
  /** Sessions stored on this device that are not uploaded yet. */
  pending: readonly StoredSession[];
  /** Last upload or save message for the panel. */
  status: string;
}

/**
 * Records QA test sessions (docs/design/qa-recording.md): a passive observer. The app calls
 * `event`, `audio`, `level` and `utterance` at its hooks; with no session running they return at
 * once. Records are buffered and written to the store every FLUSH_MS.
 */
export class QaRecorder {
  private active: Active | undefined;
  private state: QaState = { recording: false, startedAt: undefined, pending: [], status: '' };
  private readonly listeners = new Set<() => void>();

  private readonly store: QaStore;
  private readonly transport: QaTransport;
  private readonly now: () => number;

  constructor(
    store: QaStore,
    transport: QaTransport = devServerTransport,
    now: () => number = () => performance.now(),
  ) {
    this.store = store;
    this.transport = transport;
    this.now = now;
  }

  get recording(): boolean {
    return this.active !== undefined;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): QaState => this.state;

  /** Marks sessions left recording by a crash or reload as stopped, and lists pending ones. */
  async recover(): Promise<void> {
    for (const s of await this.store.sessions()) {
      if (s.state === 'recording' && s.manifest.id !== this.active?.manifest.id)
        await this.store.putSession({ ...s, state: 'stopped' });
    }
    await this.refresh();
  }

  async start(context: { age: number | null; route: string }): Promise<string> {
    if (this.active) return this.active.manifest.id;
    const started = new Date();
    const id = `${started.toISOString().slice(0, 19).replace(/:/g, '-')}-${Math.random().toString(36).slice(2, 6)}`;
    const manifest: QaManifest = {
      schema: SCHEMA_VERSION,
      id,
      startedAt: started.toISOString(),
      app: { version: __APP_VERSION__, builtAt: __BUILD_TIME__, mode: import.meta.env.MODE },
      device: deviceInfo(),
      settings: { age: context.age },
      route: context.route,
      simulated:
        typeof location !== 'undefined' &&
        new URLSearchParams(location.search).get('mic') === 'sim',
      sampleRate: QA_SAMPLE_RATE,
    };
    this.active = {
      manifest,
      t0: this.now(),
      seq: 0,
      events: 0,
      utterances: 0,
      micSamples: 0,
      eventLines: [],
      levelLines: [],
      mic: [],
      lastLevel: -Infinity,
      timer: setInterval(() => void this.flush(), FLUSH_MS),
    };
    await this.store.putSession({ manifest, state: 'recording' });
    this.event('route', { route: context.route });
    this.set({ recording: true, startedAt: this.active.t0, status: '' });
    return id;
  }

  /** The audio input as the browser applied it (sample rate, echo cancellation, gain). */
  audioInfo(info: QaManifest['audio']): void {
    if (this.active && info) this.active.manifest.audio = info;
  }

  event(type: QaEventType, data?: unknown): void {
    const a = this.active;
    if (!a) return;
    const e: QaEvent = { n: a.events++, t: Math.round(this.now() - a.t0), type };
    if (data !== undefined) e.data = data;
    a.eventLines.push(JSON.stringify(e));
  }

  /** The microphone at QA_SAMPLE_RATE, continuously (copied: callers may reuse the buffer). */
  audio(samples: Float32Array): void {
    const a = this.active;
    if (!a) return;
    a.mic.push(samples.slice());
    a.micSamples += samples.length;
  }

  level(levelDb: number, noiseDb: number): void {
    const a = this.active;
    if (!a) return;
    const t = this.now() - a.t0;
    if (t - a.lastLevel < LEVEL_MS) return;
    a.lastLevel = t;
    a.levelLines.push(
      JSON.stringify({ t: Math.round(t), level: round1(levelDb), noise: round1(noiseDb) }),
    );
  }

  /** Stores one scored utterance's audio; returns its number for the matching 'score' event. */
  utterance(audio: Float32Array): number | undefined {
    const a = this.active;
    if (!a) return undefined;
    const n = a.utterances++;
    void this.store.putChunk({
      session: a.manifest.id,
      seq: a.seq++,
      kind: 'utterance',
      utterance: n,
      audio: audio.slice(),
    });
    return n;
  }

  /** Ends the session, saves it and uploads it if this computer's dev server is reachable. */
  async stop(note?: string): Promise<void> {
    const a = this.active;
    if (!a) return;
    if (note) this.event('note', { note });
    clearInterval(a.timer);
    await this.flush();
    this.active = undefined;
    const manifest: QaManifest = {
      ...a.manifest,
      endedAt: new Date().toISOString(),
      durationMs: Math.round(this.now() - a.t0),
      ...(note ? { note } : {}),
      counts: {
        events: a.events,
        utterances: a.utterances,
        micSeconds: Math.round(a.micSamples / QA_SAMPLE_RATE),
      },
    };
    await this.store.putSession({ manifest, state: 'stopped' });
    this.set({ recording: false, startedAt: undefined, status: 'Saved on this device.' });
    await this.refresh();
    await this.upload(manifest.id);
  }

  /** Assembles a stored session into its files (session.json, events, levels, mic, utterances). */
  async files(id: string): Promise<{ name: string; data: Uint8Array<ArrayBuffer> }[]> {
    const session = (await this.store.sessions()).find((s) => s.manifest.id === id);
    if (!session) throw new Error(`no QA session ${id}`);
    const chunks = (await this.store.chunks(id)).sort((x, y) => x.seq - y.seq);
    const text = (kind: Chunk['kind']) =>
      chunks
        .filter((c) => c.kind === kind && c.text)
        .map((c) => c.text)
        .join('');
    const encoder = new TextEncoder();
    const files = [
      {
        name: 'session.json',
        data: encoder.encode(`${JSON.stringify(session.manifest, null, 1)}\n`),
      },
      { name: 'events.jsonl', data: encoder.encode(text('events')) },
      { name: 'levels.jsonl', data: encoder.encode(text('levels')) },
      {
        name: 'mic.wav',
        data: encodeWav(
          chunks.flatMap((c) => (c.kind === 'mic' && c.audio ? [c.audio] : [])),
          QA_SAMPLE_RATE,
        ),
      },
    ];
    for (const c of chunks)
      if (c.kind === 'utterance' && c.audio && c.utterance !== undefined)
        files.push({
          name: `utterances/${String(c.utterance).padStart(3, '0')}.wav`,
          data: encodeWav([c.audio], QA_SAMPLE_RATE),
        });
    return files;
  }

  /** Uploads a stopped session; it stays on the device until an upload succeeds. */
  async upload(id: string): Promise<boolean> {
    if (!(await this.transport.available())) {
      this.set({ status: 'Saved on this device. Download it, or upload from the dev server.' });
      return false;
    }
    try {
      this.set({ status: 'Uploading…' });
      for (const file of await this.files(id)) await this.transport.put(id, file.name, file.data);
      await this.store.deleteSession(id);
      this.set({ status: `Uploaded to ${this.transport.name}: ${id}` });
      await this.refresh();
      return true;
    } catch (error) {
      this.set({ status: `Upload failed (${String(error)}); the session stays on this device.` });
      await this.refresh();
      return false;
    }
  }

  /** The whole session as one ZIP file. */
  async zip(id: string): Promise<Blob> {
    const files = await this.files(id);
    return new Blob([zip(files.map((f) => ({ name: `${id}/${f.name}`, data: f.data })))], {
      type: 'application/zip',
    });
  }

  async remove(id: string): Promise<void> {
    await this.store.deleteSession(id);
    await this.refresh();
  }

  private async flush(): Promise<void> {
    const a = this.active;
    if (!a) return;
    const session = a.manifest.id;
    const writes: Chunk[] = [];
    if (a.eventLines.length)
      writes.push({ session, seq: a.seq++, kind: 'events', text: `${a.eventLines.join('\n')}\n` });
    if (a.levelLines.length)
      writes.push({ session, seq: a.seq++, kind: 'levels', text: `${a.levelLines.join('\n')}\n` });
    if (a.mic.length) {
      const audio = new Float32Array(a.mic.reduce((n, c) => n + c.length, 0));
      let at = 0;
      for (const c of a.mic) {
        audio.set(c, at);
        at += c.length;
      }
      writes.push({ session, seq: a.seq++, kind: 'mic', audio });
    }
    a.eventLines = [];
    a.levelLines = [];
    a.mic = [];
    for (const w of writes) await this.store.putChunk(w);
    // Keep the manifest current (audio settings arrive after the start).
    await this.store.putSession({ manifest: a.manifest, state: 'recording' });
  }

  private async refresh() {
    const pending = (await this.store.sessions()).filter((s) => s.state === 'stopped');
    this.set({ pending });
  }

  private set(patch: Partial<QaState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }
}

function deviceInfo(): QaManifest['device'] {
  const hasWindow = typeof window !== 'undefined';
  return {
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    language: typeof navigator === 'undefined' ? '' : navigator.language,
    screen: hasWindow ? `${String(window.screen.width)}x${String(window.screen.height)}` : '',
    pixelRatio: hasWindow ? window.devicePixelRatio : 1,
  };
}

const round1 = (v: number) => (Number.isFinite(v) ? Math.round(v * 10) / 10 : null);

/** The app's recorder. */
export const qa = new QaRecorder(
  typeof indexedDB === 'undefined' ? memoryStore() : indexedDbStore(),
);

export function useQa(): QaState {
  return useSyncExternalStore(qa.subscribe, qa.getState);
}
